import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from './AuthContext';
import type { Family, Member, Profile } from '@/types';

type FamilyRole = 'owner' | 'edit' | 'view' | null;

interface FamilyContextValue {
  family: Family | null;
  members: Member[];
  userRole: FamilyRole;
  isOwner: boolean;
  canEdit: boolean;
  loading: boolean;
  reload: () => void;
}

const FamilyContext = createContext<FamilyContextValue | null>(null);

// Fetch members for a family then attach profiles via a separate query
// (no direct FK from members.user_id → profiles, so embedded join fails)
async function fetchMembersWithProfiles(familyId: string): Promise<Member[]> {
  const { data: mems } = await supabase
    .from('members')
    .select('*')
    .eq('family_id', familyId)
    .order('created_at');

  if (!mems || mems.length === 0) return [];

  const userIds = mems.map((m) => m.user_id).filter(Boolean) as string[];
  if (userIds.length === 0) return mems as Member[];

  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, email, display_name')
    .in('id', userIds);

  const profileMap = new Map<string, Profile>();
  (profiles ?? []).forEach((p) => profileMap.set(p.id, p as Profile));

  return mems.map((m) => ({
    ...m,
    profiles: m.user_id ? (profileMap.get(m.user_id) ?? null) : null,
  })) as Member[];
}

export function FamilyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [family, setFamily] = useState<Family | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [userRole, setUserRole] = useState<FamilyRole>(null);
  const [loading, setLoading] = useState(true);
  const [rev, setRev] = useState(0);

  const reload = useCallback(() => setRev((r) => r + 1), []);

  useEffect(() => {
    if (!user) {
      setFamily(null);
      setMembers([]);
      setUserRole(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      // Check if user owns a family
      const { data: owned } = await supabase
        .from('families')
        .select('*')
        .eq('owner_id', user.id)
        .maybeSingle();

      if (owned && !cancelled) {
        const mems = await fetchMembersWithProfiles(owned.id);
        if (!cancelled) {
          setFamily(owned as Family);
          setMembers(mems);
          setUserRole('owner');
          setLoading(false);
        }
        return;
      }

      // Check active memberships
      const { data: mem } = await supabase
        .from('members')
        .select('*, families(*)')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (mem && !cancelled) {
        const fam = (mem as Member & { families: Family }).families;
        const mems = await fetchMembersWithProfiles(fam.id);
        if (!cancelled) {
          setFamily(fam);
          setMembers(mems);
          setUserRole((mem as Member).role as FamilyRole);
          setLoading(false);
        }
        return;
      }

      if (!cancelled) {
        setFamily(null);
        setMembers([]);
        setUserRole(null);
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user, rev]);

  const isOwner = userRole === 'owner';
  const canEdit = userRole === 'owner' || userRole === 'edit';

  return (
    <FamilyContext.Provider value={{ family, members, userRole, isOwner, canEdit, loading, reload }}>
      {children}
    </FamilyContext.Provider>
  );
}

export function useFamily(): FamilyContextValue {
  const ctx = useContext(FamilyContext);
  if (!ctx) throw new Error('useFamily must be inside FamilyProvider');
  return ctx;
}
